/**
 * Account Creation Logic
 *
 * Functions for creating accounts and generating IDs.
 * Part of the accounts domain (split from accountHelpers.ts).
 */

import {
  Account,
  LearnerAccount,
  ParentAccount,
  TeacherAccount,
  AdminAccount,
  AccountsState,
  AccountRole,
} from '../types'
import { suggestUsername } from './auth'
import { SUPABASE_AUTH_ENABLED } from '../../data/supabase/session'

/**
 * Cryptographically secure random hex string of `bytes * 2` characters.
 * Mirrors `authService.secureRandom*` helpers but lives here to avoid an
 * import cycle (authService → auth → ... → creation).
 */
function secureRandomHex(bytes: number): string {
  if (typeof crypto === 'undefined' || !crypto.getRandomValues) {
    throw new Error(
      'crypto.getRandomValues niet beschikbaar — kan geen veilig account-ID genereren.',
    )
  }
  const arr = new Uint8Array(bytes)
  crypto.getRandomValues(arr)
  return Array.from(arr)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Generate a UUID v4 using a cryptographically secure random source.
 * Previously used Math.random() which is not appropriate for IDs that may
 * eventually correlate with backend rows.
 */
export function generateAccountId(): string {
  // 16 random bytes → 32 hex chars; embed v4/variant bits per RFC 4122.
  const hex = secureRandomHex(16).split('')
  hex[12] = '4' // version
  // variant: 8, 9, a, or b
  const variantNibble = parseInt(hex[16], 16)
  hex[16] = ((variantNibble & 0x3) | 0x8).toString(16)
  const joined = hex.join('')
  return (
    joined.slice(0, 8) +
    '-' +
    joined.slice(8, 12) +
    '-' +
    joined.slice(12, 16) +
    '-' +
    joined.slice(16, 20) +
    '-' +
    joined.slice(20, 32)
  )
}

/**
 * Create a default admin account.
 * Used by ensureAdminAccount() as a safety-net migration.
 */
export function createDefaultAdminAccount(): AdminAccount {
  const now = Date.now()
  return {
    id: generateAccountId(),
    role: 'admin',
    displayName: 'Admin',
    createdAt: now,
    lastActive: now,
  }
}

/**
 * Zorgt dat er een adminaccount in de lokale accountstaat zit.
 *
 * ─── WAAROM DIT NIETS MEER DOET ZODRA SUPABASE AAN STAAT ────────────────────
 *
 * Dit was een migratiehulp uit de local-first tijd: bestaande opslag had nog
 * geen adminrol, dus die werd er bij het laden bijgemaakt.
 *
 * Onder Supabase-auth is dat gedrag gevaarlijk geworden. Het maakte namelijk in
 * ELKE browser een adminaccount aan — zonder wachtwoord, zonder profielrij op
 * de server, en zonder dat iemand erom vroeg. Dat account kan niet inloggen,
 * maar het staat wel in de lokale accountlijst, en alles wat door die lijst
 * loopt (accountwissel, "wie staat er op dit apparaat") zag een admin.
 *
 * Het echte gevaar zit niet in de rol zelf maar in de bron: de rol hoort uit
 * `public.profiles` te komen, waar de `profiles_update_self`-policy hem
 * vastpint. Een admin die lokaal ontstaat, omzeilt dat volledig — precies het
 * "twee waarheden"-probleem dat eerder al de verkeerde portalen opende.
 *
 * Er is bewust géén functie om jezelf tot admin te maken. De enige route blijft
 * een handmatige `update` in de SQL Editor (zie migratie 0008, sectie 4).
 */
export function ensureAdminAccount(
  accountsState: AccountsState,
): AccountsState {
  if (SUPABASE_AUTH_ENABLED) return accountsState

  const hasAdmin = Object.values(accountsState.accountsById).some(
    (account) => account.role === 'admin',
  )
  if (hasAdmin) return accountsState

  const admin = createDefaultAdminAccount()
  return {
    ...accountsState,
    accountsById: {
      ...accountsState.accountsById,
      [admin.id]: admin,
    },
  }
}

/**
 * Create a new account and add it to the accounts state.
 * Pure function — returns updated AccountsState and the new account ID.
 */
export function createAccountInState(
  accountsState: AccountsState,
  role: AccountRole,
  details: {
    displayName: string
    email?: string
    schoolName?: string
    grade?: number
    passwordHash?: string
    username?: string
    linkCode?: string
  },
  providedId?: string,
): { accounts: AccountsState; newAccountId: string } {
  if (!details || typeof details.displayName !== 'string') {
    throw new Error(
      `createAccountInState: invalid details argument (got ${typeof details}). ` +
        `Expected { displayName: string, ... }. Role: ${role}, providedId: ${providedId}`,
    )
  }
  const newId = providedId || generateAccountId()
  const now = Date.now()

  const base = {
    id: newId,
    role,
    displayName: details.displayName,
    email: details.email,
    createdAt: now,
    lastActive: now,
    passwordHash: details.passwordHash,
  }

  let newAccount: Account
  switch (role) {
    case 'teacher':
      newAccount = {
        ...base,
        role: 'teacher',
        schoolName: details.schoolName,
      } as TeacherAccount
      break
    case 'learner':
      newAccount = {
        ...base,
        role: 'learner',
        // Use canonical suggestUsername() (dot-joined, deduped, etc.) instead
        // of an inline lowercase+strip-spaces — keeps a single source of truth
        // for username normalization.
        username: details.username || suggestUsername(details.displayName),
        grade: details.grade,
        linkCode: details.linkCode,
      } as LearnerAccount
      break
    case 'parent':
      newAccount = { ...base, role: 'parent' } as ParentAccount
      break
    case 'admin':
      newAccount = { ...base, role: 'admin' } as AdminAccount
      break
  }

  return {
    accounts: {
      ...accountsState,
      accountsById: {
        ...accountsState.accountsById,
        [newId]: newAccount,
      },
    },
    newAccountId: newId,
  }
}
